import { HOME_BANNERS_LABEL, type StorefrontBanner } from "@loot-os/api-contract";
import type {
  BannerSlideContent,
  BannersContent,
} from "@loot-os/design-system/templates/home/homeContent";
import { useQuery } from "@tanstack/react-query";
import { bannersQueryOptions } from "../../../../lib/queries.js";
import { useShopper } from "../../../../lib/shopper.js";
import { useTenant } from "../../../../lib/tenant.js";

/**
 * The store's banners live for this shopper (Storefront → Banners), every slide in their order:
 * the carousel at the top of Home. Left out once they load with none, and Home shows the hero.
 */
export function useBannersContent(): BannersContent | undefined {
  const { user, authPending } = useShopper();
  const { config } = useTenant();
  // Asked once the session check settles: the answer is per viewer, so asking before it would
  // cache a signed-in shopper's banners as a visitor's, then load them again under their own key.
  const banners = useQuery({
    ...bannersQueryOptions(user?.id ?? "anon"),
    enabled: !authPending,
  });
  if (banners.isPending) return { status: "loading" };
  const slides = (banners.data ?? []).flatMap((banner) => banner.slides).flatMap(toSlideContent);
  if (slides.length === 0) return undefined;
  return { status: "ready", heading: config.name, label: HOME_BANNERS_LABEL, slides };
}

/**
 * A live slide as Home's carousel draws it: its words (the tenant's own, never rewritten), its art
 * when it has both pieces, and its call to action; none for a slide with neither art nor a title,
 * which the save refuses. The body is read unparsed, so a title from an older API is simply absent.
 */
function toSlideContent(slide: StorefrontBanner["slides"][number]): BannerSlideContent[] {
  const art =
    slide.desktopImageUrl && slide.mobileImageUrl
      ? { wide: slide.desktopImageUrl, phone: slide.mobileImageUrl, alt: slide.altText }
      : undefined;
  const title = slide.title || undefined;
  if (!art && !title) return [];
  return [
    {
      id: slide.id,
      title,
      subtitle: (title && slide.subtitle) || undefined,
      art,
      action: slide.action
        ? { label: slide.action.label, href: slide.action.url, emphasis: "primary" }
        : undefined,
    },
  ];
}
